import React, { useEffect, useMemo, useState } from 'react';
import { Badge, Button, ProgressBar } from 'react-bootstrap';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
  FaBook,
  FaGraduationCap,
  FaAward,
  FaPlay,
  FaArrowRight,
  FaClipboardList,
  FaPoll,
  FaReceipt,
  FaHeadset,
  FaLayerGroup,
  FaTrophy,
  FaMedal,
  FaLightbulb,
  FaClock,
  FaStar,
  FaUser
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import api from '../../api/apiClient';
import BadgeIcon from '../../common/components/gamification/BadgeIcon';
import { getCourseImageUrl } from '../../utils/apiBase';

const extractSkillName = (skill) => {
  if (typeof skill === 'string') return skill;
  return skill?.name
    || skill?.skill_name
    || skill?.skillName
    || skill?.title
    || skill?.label
    || skill?.Skill?.name
    || skill?.skill?.name
    || null;
};

const normalizeSkillSource = (source) => {
  if (Array.isArray(source)) return source;
  if (typeof source !== 'string') return [];

  try {
    const parsedSource = JSON.parse(source);
    if (Array.isArray(parsedSource)) return parsedSource;
  } catch {
    return source.split(',').map((value) => value.trim()).filter(Boolean);
  }

  return [];
};

const deriveCourseSkills = (course, skillCatalog = []) => {
  const skillNamesById = new Map(skillCatalog.map((skill) => [Number(skill.id), skill.name]));
  const sources = [
    course?.skills,
    course?.Skills,
    course?.CourseSkills,
    course?.skills_accrued,
    course?.skill_tags,
    course?.skill_ids,
    course?.skillIds
  ];

  return sources
    .flatMap(normalizeSkillSource)
    .map((skill) => {
      const directName = extractSkillName(skill);
      if (directName) return directName;

      const skillId = Number(typeof skill === 'object' ? skill?.skill_id || skill?.id : skill);
      return skillNamesById.get(skillId) || null;
    })
    .filter((skill, index, skills) => Boolean(skill) && skills.indexOf(skill) === index);
};

const getInstructorName = (course) => {
  const instructor = course?.instructor || course?.Instructor || course?.createdBy;
  if (!instructor) return course?.instructor_name || 'Expert Instructor';
  return instructor.name
    || `${instructor.first_name || ''} ${instructor.last_name || ''}`.trim()
    || 'Expert Instructor';
};

const formatCourseDuration = (duration) => {
  if (!duration) return 'Self-paced';
  if (typeof duration === 'number' || /^\d+$/.test(String(duration))) return `${duration} mins`;
  return String(duration);
};

const UserDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState({
    enrolledCourses: [],
    availableCourses: [],
    availableCoursesCount: 0,
    certificatesEarned: 0,
    courseProgressById: {},
    assessments: { assigned: 0, attempted: 0, passed: 0, failed: 0 },
    surveys: { available: 0, totalQuestions: 0 },
    transactions: { total: 0, success: 0, pending: 0, failed: 0, totalSpent: 0 },
    supportTickets: { total: 0, open: 0, in_progress: 0, resolved: 0, closed: 0 },
    gamification: null,
    skillCatalog: [],
    acquiredSkills: []
  });

  useEffect(() => {
    const loadDashboardData = async () => {
      if (!user?.id) {
        return;
      }

      try {
        setLoading(true);

        const [
          enrolledResponse,
          coursesResponse,
          certificatesResponse,
          scormSummaryResponse,
          assessmentsResponse,
          surveysResponse,
          transactionsResponse,
          supportTicketsResponse,
          gamificationResponse,
          skillCatalogResponse,
          skillsResponse
        ] = await Promise.allSettled([
          api.enrollments.getByUser(user.id),
          api.courses.getAll(),
          api.certificates.getLearnerCertificates(user.id, { page: 1, limit: 1 }),
          api.scormTracking.getUserSummary(user.id),
          api.assessments.getUserAssessments(),
          api.surveys.getAvailable(),
          api.payments.listMy(),
          api.supportTickets.getAll({ page: 1, limit: 200 }),
          api.gamification.getMyProfile(),
          api.skills.list(),
          api.skills.getMyAcquired()
        ]);

        const enrolledCoursesRaw = enrolledResponse.status === 'fulfilled'
          ? (enrolledResponse.value.data?.data || enrolledResponse.value.data || [])
          : [];
        const enrolledCourses = Array.isArray(enrolledCoursesRaw) ? enrolledCoursesRaw : [];

        const allCoursesRaw = coursesResponse.status === 'fulfilled'
          ? (coursesResponse.value.data?.data || [])
          : [];
        const allCourses = Array.isArray(allCoursesRaw) ? allCoursesRaw : [];

        const enrolledCourseIds = new Set(
          enrolledCourses.map((item) => Number(item.course_id || item.Course?.id || item.id)).filter(Boolean)
        );
        const availableCourses = allCourses.filter((course) => !enrolledCourseIds.has(Number(course.id)));
        const availableCoursesCount = availableCourses.length;

        const certificatesEarned = certificatesResponse.status === 'fulfilled'
          ? Number(
              certificatesResponse.value.data?.data?.pagination?.totalItems
              || certificatesResponse.value.data?.data?.pagination?.total
              || 0
            )
          : 0;

        const scormRecordsRaw = scormSummaryResponse.status === 'fulfilled'
          ? (scormSummaryResponse.value.data?.data?.courses || [])
          : [];
        const scormRecords = Array.isArray(scormRecordsRaw) ? scormRecordsRaw : [];

        const courseProgressById = scormRecords.reduce((acc, record) => {
          const courseId = Number(record.course_id);
          if (!courseId) {
            return acc;
          }

          const status = String(record.lesson_status || '').toLowerCase();
          const isCompleted = status === 'completed' || status === 'passed';
          const rawProgress = Number(record.progress_measure || 0);
          const normalizedProgress = rawProgress <= 1 ? rawProgress * 100 : rawProgress;
          const computedProgress = isCompleted
            ? 100
            : Math.max(0, Math.min(100, Number.isFinite(normalizedProgress) ? normalizedProgress : 0));

          if (!acc[courseId] || computedProgress > acc[courseId]) {
            acc[courseId] = computedProgress;
          }
          return acc;
        }, {});

        const assessmentSummary = assessmentsResponse.status === 'fulfilled'
          ? (assessmentsResponse.value.data?.summary || {})
          : {};

        const surveys = surveysResponse.status === 'fulfilled'
          ? (surveysResponse.value.data?.data || [])
          : [];
        const surveyList = Array.isArray(surveys) ? surveys : [];

        const paymentsRaw = transactionsResponse.status === 'fulfilled'
          ? (transactionsResponse.value.data?.data || transactionsResponse.value.data || [])
          : [];
        const payments = Array.isArray(paymentsRaw) ? paymentsRaw : [];

        const successfulPayments = payments.filter((payment) => String(payment.status || '').toLowerCase() === 'success');
        const pendingPayments = payments.filter((payment) => String(payment.status || '').toLowerCase() === 'pending');
        const failedPayments = payments.filter((payment) => String(payment.status || '').toLowerCase() === 'failed');
        const totalSpent = successfulPayments.reduce(
          (sum, payment) => sum + Number(payment.final_amount ?? payment.amount ?? 0),
          0
        );

        const ticketList = supportTicketsResponse.status === 'fulfilled'
          ? (supportTicketsResponse.value.data?.data?.tickets || [])
          : [];
        const tickets = Array.isArray(ticketList) ? ticketList : [];

        const supportTickets = tickets.reduce(
          (acc, ticket) => {
            const status = String(ticket.status || '').toLowerCase();
            if (status in acc) {
              acc[status] += 1;
            }
            return acc;
          },
          { total: tickets.length, open: 0, in_progress: 0, resolved: 0, closed: 0 }
        );

        const gamification = gamificationResponse.status === 'fulfilled' && gamificationResponse.value.data?.success
          ? (gamificationResponse.value.data.data || null)
          : null;

        const acquiredSkillsRaw = skillsResponse.status === 'fulfilled' && skillsResponse.value.data?.success
          ? (skillsResponse.value.data.data || [])
          : [];
        const acquiredSkills = Array.isArray(acquiredSkillsRaw) ? acquiredSkillsRaw : [];
        const skillCatalogRaw = skillCatalogResponse.status === 'fulfilled' && skillCatalogResponse.value.data?.success
          ? (skillCatalogResponse.value.data.data || [])
          : [];
        const skillCatalog = Array.isArray(skillCatalogRaw) ? skillCatalogRaw : [];

        setDashboardData({
          enrolledCourses,
          availableCourses,
          availableCoursesCount,
          certificatesEarned,
          courseProgressById,
          assessments: {
            assigned: Number(assessmentSummary.assigned_count || 0),
            attempted: Number(assessmentSummary.attempted_assessment_count || 0),
            passed: Number(assessmentSummary.passed_count || 0),
            failed: Number(assessmentSummary.failed_count || 0)
          },
          surveys: {
            available: surveyList.length,
            totalQuestions: surveyList.reduce((sum, survey) => sum + Number(survey.question_count || 0), 0)
          },
          transactions: {
            total: payments.length,
            success: successfulPayments.length,
            pending: pendingPayments.length,
            failed: failedPayments.length,
            totalSpent: Number(totalSpent.toFixed(2))
          },
          supportTickets,
          gamification,
          skillCatalog,
          acquiredSkills
        });
      } catch (error) {
        toast.error('Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };

    loadDashboardData();
  }, [user?.id]);

  const enrolledWithProgress = useMemo(() => {
    return dashboardData.enrolledCourses
      .map((item) => {
        const course = item.Course || item;
        const courseId = Number(item.course_id || course?.id || item.id);
        const progress = Number(dashboardData.courseProgressById[courseId] || 0);
        return {
          id: courseId,
          title: course?.title || course?.name || 'Course',
          description: course?.description || '',
          image: course?.course_image || course?.image || course?.thumbnail || '',
          category: course?.category || 'Professional Learning',
          difficulty: course?.difficulty_level || course?.difficulty || 'All Levels',
          duration: course?.duration || course?.estimated_duration || null,
          instructor: getInstructorName(course),
          skills: deriveCourseSkills(course, dashboardData.skillCatalog),
          progress
        };
      })
      .filter((item) => Boolean(item.id))
      .sort((a, b) => b.progress - a.progress);
  }, [dashboardData.courseProgressById, dashboardData.enrolledCourses, dashboardData.skillCatalog]);

  const enrolledCount = enrolledWithProgress.length;
  const completedCount = enrolledWithProgress.filter((item) => item.progress >= 100).length;
  const overallCompletion = enrolledCount > 0 ? Math.round((completedCount / enrolledCount) * 100) : 0;
  const gamification = dashboardData.gamification;
  const earnedBadges = (gamification?.badges_earned || []).slice(0, 6);
  const recommendedCourses = useMemo(() => {
    return dashboardData.availableCourses.map((course) => ({
      id: Number(course.id),
      slug: course.slug,
      title: course.title || course.name || 'Course',
      description: course.description || '',
      image: course.course_image || course.image || course.thumbnail || '',
      category: (typeof course.category === 'string' ? course.category : course.category?.name)
        || course.Category?.name
        || 'Professional Learning',
      difficulty: course.difficulty_level || course.difficulty || 'All Levels',
      duration: course.duration || course.estimated_duration || null,
      instructor: getInstructorName(course),
      skills: deriveCourseSkills(course, dashboardData.skillCatalog)
    }));
  }, [dashboardData.availableCourses, dashboardData.skillCatalog]);
  const recentlyCompletedCourse = enrolledWithProgress.find((course) => course.progress >= 100);
  const recommendationReason = recentlyCompletedCourse
    ? `Recommended because you completed ${recentlyCompletedCourse.title}`
    : 'Recommended to continue building your learning momentum';

  const statsData = [
    { title: 'Enrolled Courses', value: enrolledCount, icon: FaBook, gradient: 'from-purple-600 via-pink-600 to-rose-600' },
    { title: 'Completed Courses', value: completedCount, icon: FaGraduationCap, gradient: 'from-indigo-600 via-purple-600 to-pink-600' },
    { title: 'Certificates Earned', value: dashboardData.certificatesEarned, icon: FaAward, gradient: 'from-amber-500 via-orange-500 to-rose-600' },
    { title: 'Available Courses', value: dashboardData.availableCoursesCount, icon: FaLayerGroup, gradient: 'from-cyan-500 via-blue-600 to-indigo-600' }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-indigo-50 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-16 w-16 border-4 border-purple-600 border-t-transparent mb-4"></div>
          <p className="text-gray-600 text-lg font-medium">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-indigo-50 p-4 sm:p-6 md:p-8 lg:p-10 min-h-[calc(100vh-100px)] flex flex-col gap-1 sm:gap-6 md:gap-8">
      <div className="mb-6 sm:mb-10">
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl sm:rounded-3xl p-6 sm:p-8 border border-purple-100 shadow-lg hover:shadow-2xl transition-all duration-300">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 md:gap-6">
            <div className="flex-1">
              <h2 className="text-left text-2xl sm:text-3xl md:text-3xl font-bold text-gray-900 mb-2">
                Welcome back, {user?.first_name || user?.name || 'Student'}! 👋
              </h2>
              <p className="text-gray-600 text-base sm:text-lg">
                Here is your live learning snapshot with progress, score, badges, skills, assessments, and support updates.
              </p>
            </div>
            <Button
              bsPrefix="custom-hamburger"
              className="border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2  custom-hamburger custom-hamburger-primary p-2"
              size="lg"
              onClick={() => navigate('/user/enrolled-courses')}
            >
              <FaPlay className="text-sm" />
              Continue Learning
            </Button>
          </div>
        </div>
      </div>

      <section className="mb-8 grid grid-cols-1 gap-5 md:grid-cols-[minmax(0,65fr)_minmax(250px,35fr)] xl:grid-cols-[minmax(0,3fr)_minmax(270px,1fr)]" aria-label="Recommended learning and statistics">
        <article className="overflow-hidden rounded-3xl border border-purple-100 bg-white shadow-xl">
          <div className="flex items-center justify-between border-b border-purple-100 bg-gradient-to-r from-purple-50 via-white to-pink-50 px-5 py-4 sm:px-6">
            <div>
              <h3 className="mb-1 text-xl font-extrabold text-gray-900">Recommended Courses</h3>
              <p className="mb-0 text-sm text-gray-600">Discover your next learning opportunity</p>
            </div>
            <Badge className="!rounded-full !bg-gradient-to-r !from-pink-500 !to-purple-600 px-3 py-2 text-white shadow">
              {recommendedCourses.length} Available
            </Badge>
          </div>

          {recommendedCourses.length > 0 ? (
            <div className="max-h-[520px] space-y-4 overflow-y-auto p-4 pr-3 [scrollbar-color:#a855f7_#f3e8ff] [scrollbar-width:thin] sm:p-5 sm:pr-4">
              {recommendedCourses.map((course, index) => (
                <div
                  key={course.id}
                  className="group relative flex flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:border-purple-300 hover:shadow-lg sm:flex-row"
                >
                  <div className="relative h-36 flex-none overflow-hidden bg-gradient-to-br from-purple-600 to-pink-600 sm:h-auto sm:w-44">
                    {course.image ? (
                      <img
                        src={getCourseImageUrl(course.image)}
                        alt={course.title}
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center">
                        <FaGraduationCap className="text-5xl text-white/80" aria-hidden="true" />
                      </div>
                    )}
                    {index === 0 && (
                      <Badge className="absolute left-3 top-3 !rounded-full !bg-pink-600 px-2.5 py-1.5 text-[10px] font-bold text-white shadow">
                        Next Course
                      </Badge>
                    )}
                  </div>

                  <div className="min-w-0 flex-1 p-4">
                    <p className="mb-1 text-[11px] font-bold uppercase tracking-wider text-purple-600">{course.category}</p>
                    <h4 className="mb-2 line-clamp-1 text-lg font-bold text-gray-900">{course.title}</h4>
                    <p className="mb-3 flex items-start gap-2 text-xs text-amber-800">
                      <FaStar className="mt-0.5 flex-none text-amber-500" aria-hidden="true" />
                      {recommendationReason}
                    </p>

                    <div className="mb-3 flex flex-wrap items-center gap-1.5">
                      <span className="text-[11px] font-semibold text-gray-500">Skills:</span>
                      {course.skills.length > 0 ? course.skills.slice(0, 3).map((skill) => (
                        <Badge key={skill} className="!rounded-full !bg-gradient-to-r !from-purple-600 !to-pink-600 px-2.5 py-1 !text-[11px] !font-semibold !text-white">
                          {skill}
                        </Badge>
                      )) : (
                        <Badge className="!rounded-full !bg-gray-500 px-2.5 py-1 !text-[11px] !font-medium !text-white">
                          Skills not assigned
                        </Badge>
                      )}
                      {course.skills.length > 3 && (
                        <Badge className="!rounded-full !bg-pink-600 px-2.5 py-1 !text-[11px] !font-semibold !text-white">
                          +{course.skills.length - 3}
                        </Badge>
                      )}
                      <Badge className="!rounded-full !bg-indigo-600 px-2.5 py-1 !text-[11px] !font-semibold !text-white">
                        {course.difficulty}
                      </Badge>
                    </div>

                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
                        <span className="flex items-center gap-1.5">
                          <FaClock className="text-purple-500" aria-hidden="true" />
                          {formatCourseDuration(course.duration)}
                        </span>
                        <span className="flex items-center gap-1.5">
                          <FaUser className="text-purple-500" aria-hidden="true" />
                          {course.instructor}
                        </span>
                      </div>
                      <Button
                        bsPrefix="custom-hamburger"
                        className="flex flex-none items-center justify-center gap-2 rounded-lg border-0 bg-gradient-to-r from-purple-600 to-pink-600 px-4 py-2 text-xs font-bold text-white shadow-md transition-all hover:from-purple-700 hover:to-pink-700 hover:shadow-lg"
                        onClick={() => navigate(course.slug ? `/courses/${course.slug}` : '/user/courses')}
                      >
                        View Course <FaArrowRight aria-hidden="true" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex min-h-96 flex-col items-center justify-center p-8 text-center">
              <span className="mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-purple-100 text-2xl text-purple-600">
                <FaGraduationCap aria-hidden="true" />
              </span>
              <h3 className="mb-2 text-xl font-bold text-gray-900">You are enrolled in every available course</h3>
              <p className="mb-0 text-gray-600">New recommendations will appear here when more courses are published.</p>
            </div>
          )}
        </article>

        <aside className="flex flex-col gap-3" aria-label="Learning statistics">
          {statsData.map((stat) => {
            const IconComponent = stat.icon;
            return (
              <div
                key={stat.title}
                className="flex min-h-24 flex-1 items-center gap-4 rounded-2xl border border-purple-100 bg-white p-4 shadow-lg transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl"
              >
                <span className={`flex h-14 w-14 flex-none items-center justify-center rounded-2xl bg-gradient-to-br ${stat.gradient} text-xl text-white shadow-md`}>
                  <IconComponent aria-hidden="true" />
                </span>
                <div className="min-w-0 flex-grow">
                  <p className="mb-0 text-3xl font-extrabold leading-none text-gray-900">{Number(stat.value || 0)}</p>
                  <p className="mb-0 mt-2 text-sm font-medium text-gray-600">{stat.title}</p>
                </div>
              </div>
            );
          })}
        </aside>
      </section>

      {/* Score & Badges */}
      <div className="bg-white/80 backdrop-blur-sm rounded-2xl sm:rounded-3xl border border-purple-100 shadow-lg hover:shadow-2xl transition-all duration-300 mb-6 sm:mb-8">
        <div className="p-4 sm:p-6 md:p-8 border-b border-purple-100">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h5 className="text-xl sm:text-2xl font-bold text-gray-900 mb-0 flex items-center gap-2">
                <FaTrophy className="text-amber-500" />
                Your Score & Badges
              </h5>
              <p className="text-gray-600 text-sm mb-0 mt-1">
                Track your learning points and earned badge levels
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                bsPrefix="custom-hamburger"
                variant="link"
                className="p-0 text-emerald-600 hover:text-emerald-700 font-semibold text-sm flex items-center gap-1"
                onClick={() => navigate('/user/badges')}
              >
                View Badges <FaArrowRight className="text-xs" />
              </Button>
              <Button
                bsPrefix="custom-hamburger"
                variant="link"
                className="p-0 text-purple-600 hover:text-purple-700 font-semibold text-sm flex items-center gap-1 ms-3"
                onClick={() => navigate('/user/leaderboard')}
              >
                Leaderboard <FaArrowRight className="text-xs" />
              </Button>
            </div>
          </div>
        </div>
        <div className="p-4 sm:p-6 md:p-8">
          {!gamification ? (
            <p className="text-gray-600 mb-0">Score and badges will appear once you start earning learning points.</p>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-1 rounded-2xl bg-gradient-to-br from-purple-50 to-indigo-50 border border-purple-100 p-5">
                <p className="text-xs uppercase tracking-wider text-purple-500 font-semibold mb-3">Current Score</p>
                <div className="flex items-center gap-4 mb-4">
                  <div
                    className="w-16 h-16 rounded-2xl flex items-center justify-center shadow"
                    style={{ backgroundColor: `${gamification.badge_color || '#8b5cf6'}22`, color: gamification.badge_color || '#8b5cf6' }}
                  >
                    <BadgeIcon
                      badgeKey={gamification.badge_key}
                      color={gamification.badge_color || '#8b5cf6'}
                      size={32}
                    />
                  </div>
                  <div>
                    <h3 className="text-3xl font-bold text-gray-900 mb-0">{Number(gamification.total_points || 0)}</h3>
                    <p className="text-sm text-gray-600 mb-0">Total Points</p>
                  </div>
                </div>
                <p className="font-semibold text-gray-900 mb-1">{gamification.current_level || 'Newbie'}</p>
                <p className="text-sm text-gray-600 mb-3">{gamification.current_badge || 'Starting level'}</p>
                {gamification.next_level && (
                  <div>
                    <div className="flex justify-between text-xs text-gray-600 mb-1">
                      <span>Progress to {gamification.next_level.level_name}</span>
                      <span className="font-semibold">{gamification.progress_percent || 0}%</span>
                    </div>
                    <ProgressBar
                      now={Number(gamification.progress_percent || 0)}
                      className="rounded-pill"
                      style={{ height: 8 }}
                    />
                    <p className="text-xs text-gray-500 mt-2 mb-0">
                      {gamification.points_to_next_level || 0} points to next level
                    </p>
                  </div>
                )}
              </div>

              <div className="lg:col-span-2">
                <div className="flex items-center gap-2 mb-4">
                  <FaMedal className="text-rose-500" />
                  <h6 className="text-lg font-bold text-gray-900 mb-0">Earned Badges</h6>
                </div>
                {earnedBadges.length === 0 ? (
                  <p className="text-gray-600 mb-0">No badges earned yet. Keep learning to unlock your first badge.</p>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
                    {earnedBadges.map((badge) => (
                      <div
                        key={badge.id || badge.badge_key}
                        className={`rounded-xl border p-3 flex items-center gap-3 ${
                          badge.is_current ? 'border-purple-300 bg-purple-50' : 'border-gray-200 bg-gray-50'
                        }`}
                      >
                        <div
                          className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
                          style={{ backgroundColor: `${badge.badge_color || '#9CA3AF'}22`, color: badge.badge_color || '#9CA3AF' }}
                        >
                          <BadgeIcon
                            badgeKey={badge.badge_key}
                            color={badge.badge_color || '#9CA3AF'}
                            size={22}
                          />
                        </div>
                        <div className="min-w-0">
                          <p className="font-semibold text-gray-900 mb-0 truncate">
                            {badge.badge_label || badge.level_name}
                          </p>
                          <p className="text-xs text-gray-500 mb-0">
                            {badge.is_current ? 'Current badge' : `Unlocked ${badge.unlocked_at ? new Date(badge.unlocked_at).toLocaleDateString() : ''}`}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Skills Acquired */}
      <div className="bg-white/80 backdrop-blur-sm rounded-2xl sm:rounded-3xl border border-purple-100 shadow-lg hover:shadow-2xl transition-all duration-300 mb-6 sm:mb-8">
        <div className="p-4 sm:p-6 md:p-8 border-b border-purple-100">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h5 className="text-xl sm:text-2xl font-bold text-gray-900 mb-0 flex items-center gap-2">
                <FaLightbulb className="text-amber-500" />
                Skills Acquired
              </h5>
              <p className="text-gray-600 text-sm mb-0 mt-1">
                Skills unlocked automatically when you complete a course
              </p>
            </div>
            <Badge className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white px-3 py-2 rounded-full">
              {dashboardData.acquiredSkills.length} skill{dashboardData.acquiredSkills.length === 1 ? '' : 's'}
            </Badge>
          </div>
        </div>
        <div className="p-4 sm:p-6 md:p-8">
          {dashboardData.acquiredSkills.length === 0 ? (
            <div className="text-center py-6">
              <p className="text-gray-600 mb-2">No skills acquired yet.</p>
              <p className="text-sm text-gray-500 mb-0">
                Complete a course that has linked skills to see them here.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              {dashboardData.acquiredSkills.map((skill) => (
                <div
                  key={skill.id || `${skill.skill_id}-${skill.course_id}`}
                  className="rounded-xl border border-indigo-100 bg-gradient-to-br from-indigo-50 to-white p-4 shadow-sm"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 text-white flex items-center justify-center shrink-0">
                      <FaLightbulb />
                    </div>
                    <div className="min-w-0">
                      <p className="font-semibold text-gray-900 mb-1 truncate">{skill.name}</p>
                      {skill.course_name && (
                        <p className="text-xs text-gray-500 mb-1 truncate">From: {skill.course_name}</p>
                      )}
                      {skill.earned_at && (
                        <p className="text-xs text-indigo-600 mb-0">
                          Earned {new Date(skill.earned_at).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8 md:gap-10 lg:gap-12">
        <div className="lg:col-span-2">
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl sm:rounded-3xl border border-purple-100 shadow-lg hover:shadow-2xl transition-all duration-300">
            <div className="p-4 sm:p-6 md:p-8 border-b border-purple-100">
              <div className="flex justify-between items-center">
                <div>
                  <h5 className="text-xl sm:text-2xl font-bold text-gray-900 mb-0">Enrolled Course Progress</h5>
                  <p className="text-gray-600 text-sm mb-0 mt-1">Overall Completion: {overallCompletion}%</p>
                </div>
                <Button
                  bsPrefix="custom-hamburger"
                  variant="link"
                  className="p-0 text-emerald-600 hover:text-emerald-700 font-semibold text-sm sm:text-base flex items-center gap-1"
                  onClick={() => navigate('/user/enrolled-courses')}
                >
                  View All Courses <FaArrowRight className="text-xs" />
                </Button>
              </div>
            </div>
            <div className="p-5 sm:p-6 md:p-8">
              {enrolledWithProgress.length === 0 ? (
                <div className="text-center py-6">
                  <p className="text-gray-600 mb-0">No enrolled courses found.</p>
                </div>
              ) : enrolledWithProgress.slice(0, 6).map((course, index) => (
                <div
                  key={index}
                  className={`p-4 sm:p-5 rounded-xl mb-4 last:mb-0 bg-gradient-to-br from-gray-50 to-white border border-gray-200 hover:border-purple-300 transition-all duration-200 ${index === enrolledWithProgress.length - 1 ? '' : 'border-b'}`}
                >
                  <div className="flex flex-col md:flex-row md:items-center gap-4">
                    <div className="flex-1">
                      <h6 className="text-lg font-bold text-gray-900 mb-2">{course.title}</h6>
                      <div className="mb-2">
                        <div className="flex justify-between text-xs text-gray-600 mb-1">
                          <span>Progress</span>
                          <span className="font-semibold">{course.progress}% completed</span>
                        </div>
                        <div className="w-full h-2.5 bg-gray-200 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-gradient-to-r from-emerald-500 to-teal-600 rounded-full transition-all duration-500"
                            style={{ width: `${course.progress}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                    <div className="md:ml-4">
                      <Button
                        bsPrefix="custom-hamburger"
                        className="w-full border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2 px-3"
                        size="sm"
                        onClick={() => navigate('/user/enrolled-courses')}
                      >
                        <FaPlay className="text-xs" />
                        Continue
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl sm:rounded-3xl border border-purple-100 shadow-lg hover:shadow-2xl transition-all duration-300">
            <div className="p-4 sm:p-6 md:p-8 border-b border-purple-100">
              <h6 className="text-lg sm:text-xl font-bold text-gray-900 mb-0">Assessment Info</h6>
            </div>
            <div className="p-4 sm:p-6 md:p-8">
              <div className="flex items-center mb-4 pb-4 border-b border-gray-100 last:border-0 last:mb-0 last:pb-0">
                <FaClipboardList className="text-purple-600 mr-3" />
                <div className="w-full flex justify-between"><span>Assigned</span><strong>{dashboardData.assessments.assigned}</strong></div>
              </div>
              <div className="flex items-center mb-4 pb-4 border-b border-gray-100 last:border-0 last:mb-0 last:pb-0">
                <FaClipboardList className="text-blue-600 mr-3" />
                <div className="w-full flex justify-between"><span>Attempted</span><strong>{dashboardData.assessments.attempted}</strong></div>
              </div>
              <div className="flex items-center">
                <FaClipboardList className="text-emerald-600 mr-3" />
                <div className="w-full flex justify-between"><span>Passed / Failed</span><strong>{dashboardData.assessments.passed} / {dashboardData.assessments.failed}</strong></div>
              </div>
            </div>
          </div>

          <div className="bg-white/80 backdrop-blur-sm rounded-2xl sm:rounded-3xl border border-purple-100 shadow-lg hover:shadow-2xl transition-all duration-300">
            <div className="p-4 sm:p-6 md:p-8 border-b border-purple-100">
              <h6 className="text-lg sm:text-xl font-bold text-gray-900 mb-0">Activity Summary</h6>
            </div>
            <div className="p-4 sm:p-6 md:p-8">
              <div className="space-y-2 mb-4">
                <div className="flex items-center justify-between text-sm"><span className="flex items-center gap-2"><FaPoll className="text-pink-500" /> Surveys Available</span><strong>{dashboardData.surveys.available}</strong></div>
                <div className="flex items-center justify-between text-sm"><span className="flex items-center gap-2"><FaPoll className="text-pink-500" /> Survey Questions</span><strong>{dashboardData.surveys.totalQuestions}</strong></div>
                <div className="flex items-center justify-between text-sm"><span className="flex items-center gap-2"><FaReceipt className="text-cyan-500" /> Transactions</span><strong>{dashboardData.transactions.total}</strong></div>
                <div className="flex items-center justify-between text-sm"><span className="flex items-center gap-2"><FaReceipt className="text-cyan-500" /> Total Spent</span><strong>₹{dashboardData.transactions.totalSpent}</strong></div>
                <div className="flex items-center justify-between text-sm"><span className="flex items-center gap-2"><FaHeadset className="text-red-500" /> Support Tickets</span><strong>{dashboardData.supportTickets.total}</strong></div>
                <div className="flex items-center justify-between text-sm"><span>Open / In Progress</span><strong>{dashboardData.supportTickets.open} / {dashboardData.supportTickets.in_progress}</strong></div>
              </div>

              <div className="flex flex-wrap gap-2">
                <Badge className="bg-gradient-to-r from-emerald-500 to-teal-600 text-white px-3 py-2 rounded-full">Txn Success: {dashboardData.transactions.success}</Badge>
                <Badge className="bg-gradient-to-r from-amber-500 to-yellow-600 text-white px-3 py-2 rounded-full">Txn Pending: {dashboardData.transactions.pending}</Badge>
                <Badge className="bg-gradient-to-r from-red-500 to-rose-600 text-white px-3 py-2 rounded-full">Txn Failed: {dashboardData.transactions.failed}</Badge>
              </div>

              <div className="space-y-3 mt-4">
                <Button
                  bsPrefix="custom-hamburger"
                  variant="outline-primary"
                  className="w-full border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-left gap-2 p-2 "
                  onClick={() => navigate('/user/courses')}
                >
                  <FaBook className="text-sm" />
                   Browse Courses
                </Button>
                <Button
                  bsPrefix="custom-hamburger"
                  variant="outline-success"
                  className="w-full border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-left gap-2 p-2 "
                  onClick={() => navigate('/user/assessments')}
                >
                  <FaClipboardList className="text-sm" />
                  Assessments
                </Button>
                <Button
                  bsPrefix="custom-hamburger"
                  variant="outline-info"
                  className="w-full border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-left gap-2 p-2 "
                  onClick={() => navigate('/user/surveys')}
                >
                  <FaPoll className="text-sm" />
                  Surveys
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserDashboard;
